import { Injectable } from '@angular/core';
import { RxEmitter } from '../rxjs/rx-emitter';
import { MessageBusEvents } from '../common/constants/message-bus-events';
import { BcRestApi } from '../api/rest-api';

const RETRY_COUNT = 120;
const RETRY_TIMEOUT = 500;

@Injectable()
export class ChatMessagesQueueService {
  private _chatMessageQueues = {};
  private _chatMessageSubscriptions = {};

  constructor(private _rxEmitter: RxEmitter,
              private _bcRestApi: BcRestApi) {}

  public getChatMessagesInQueue(chatId: string) {
    return this._chatMessageQueues[chatId] ? this._chatMessageQueues[chatId].messages : [];
  }

  public deleteChatMessageFromQueue(chatId: string, messageIndex: number) {
    this._chatMessageQueues[chatId].messages.splice(messageIndex, 1);

    this._rxEmitter.send(`${MessageBusEvents.PendingChatMessages}${chatId}`, null);

    if (this._chatMessageQueues[chatId].messages.length === 0) {
      this._rxEmitter.send(`${MessageBusEvents.ChatMessagesQueueCleared}${chatId}`, null);
    }
  }

  public createSubscriptionForChat(chatId: string): void {
    if (!this._chatMessageSubscriptions[chatId]) {
      this._chatMessageSubscriptions[chatId] = this._rxEmitter.subscribe(`${MessageBusEvents.ChatMessagesQueue}${chatId}`, (chatMessage) => {
        if (!this._chatMessageQueues[chatId]) {
          this._chatMessageQueues[chatId] = {
            messages: []
          };
        }

        this._chatMessageQueues[chatId].messages.push(chatMessage);
        this._processMessages(chatId);
      });
    }
  }

  private async _processMessages(chatId: string): Promise<void> {
    if (!this._chatMessageQueues[chatId].isProcessing) {
      this._chatMessageQueues[chatId].isProcessing = true;
      while (this._chatMessageQueues[chatId].messages && this._chatMessageQueues[chatId].messages.length) {
        await this._retrySend(chatId, RETRY_COUNT, this._chatMessageQueues[chatId].messages[0]);
        this._chatMessageQueues[chatId].messages.shift();
      }

      this._chatMessageQueues[chatId].isProcessing = false;
      this._rxEmitter.send(`${MessageBusEvents.ChatMessagesQueueCleared}${chatId}`, null);
      this._clearMessageSubscription(chatId);
    }
  }

  private async _retrySend(chatId: string, retryCount: number, chatMessage: string) {
    try {
      return await this._bcRestApi.sendMessage(chatMessage);
    } catch (error) {
      if (retryCount === 0) {
        this._chatMessageQueues[chatId].messages = [];
        this._chatMessageQueues[chatId].isProcessing = false;
        this._rxEmitter.send(`${MessageBusEvents.ChatMessagesQueueCleared}${chatId}`, null);
        throw error;
      }

      this._rxEmitter.send(`${MessageBusEvents.PendingChatMessages}${chatId}`, null);

      return new Promise((resolve) => {
        setTimeout(async () => {
          await this._retrySend(chatId, retryCount - 1, chatMessage);
          resolve({});
        }, RETRY_TIMEOUT);
      });
    }
  }

  private _clearMessageSubscription(chatId: string): void {
    if (this._chatMessageSubscriptions && this._chatMessageSubscriptions[chatId]) {
      this._chatMessageSubscriptions[chatId].unsubscribe();
      this._chatMessageSubscriptions[chatId] = null;
    }
  }
}
